import { ChevronRight } from "lucide-react";

import { Button } from "@/components/button";

import type { MBCUIMessage } from "../types/mbc-chat.types";
import type { MbcUploadBinding } from "./parts/UploadPromptPart";

import { MBCChatInput } from "./MBCChatInput";
import { MBCMessageList } from "./MBCMessageList";

type MBCChatPanelProps = {
  canReview?: boolean;
  isStreaming: boolean;
  /** An in-chat upload is still running; leaving for Review would lose it. */
  isUploading?: boolean;
  messages: MBCUIMessage[];
  onAttach?: () => void;
  onReview: () => void;
  onSelectChoice: (label: string) => void;
  onSend: (text: string) => void;
  status: string;
  /** What the in-chat upload block needs to write the brief. */
  upload?: MbcUploadBinding;
};

export function MBCChatPanel({
  canReview = false,
  isStreaming,
  isUploading = false,
  messages,
  onAttach,
  onReview,
  onSelectChoice,
  onSend,
  status,
  upload,
}: MBCChatPanelProps) {
  const isInputDisabled = status !== "ready";

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl border border-border-secondary bg-bg-secondary">
      <h2 className="px-md pt-xl text-center lg:px-3xl text-lg font-bold text-text-primary">
        Let&apos;s build your brief together
      </h2>
      <MBCMessageList
        isInteractiveDisabled={isInputDisabled}
        isStreaming={isStreaming}
        isUploading={isUploading}
        messages={messages}
        onReview={onReview}
        onSelectChoice={onSelectChoice}
        upload={upload}
      />
      {canReview ? (
        <div className="flex flex-col gap-xs border-t border-border-secondary px-md py-md lg:px-3xl">
          <Button
            className="w-full gap-sm"
            isDisabled={isUploading}
            onPress={onReview}
            variant="primary"
          >
            Let&apos;s review the brief!
            <ChevronRight className="size-4" />
          </Button>
          {isUploading ? (
            <span className="text-center text-sm text-text-tertiary">
              Wait for uploads to finish
            </span>
          ) : null}
        </div>
      ) : null}
      <MBCChatInput
        isDisabled={isInputDisabled}
        onAttach={onAttach}
        onSend={onSend}
      />
    </section>
  );
}
